import * as auth from '@/actions/auth'
import { AlertMessage, NavigationPage } from '@/app/components'
import LinkDiagram from '@/app/features/logos/LinkDiagram'
import { useNavigation } from '@/app/hooks'
import { useInitialDataLoader } from '@/authenticated/App'
import {
  Button,
  Flex,
  Icon,
  IconDetailListItem,
  List,
  Text,
} from '@/components'
import { IconNames } from '@/components/Icon/Icon'
import { useAppDispatch, useAppSelector, useUrlSearchParam } from '@/hooks'
import clsx from 'clsx'
import Car from '../car.png'
import OptiwattLogo from '../optiwatt-logo.webp'
import ExistingUserModalSurvey, { ExistingUserModal } from './existing-user'
import { fetchAuthenticated } from '@/utils/fetch'
import * as Sentry from '@sentry/react'
import { useProgramSpecificConfigFromUrlParam } from '@/app/hooks/useProgramSpecificConfigFromUrlParam'
import PageLoader from '@/authenticated/components/layout/PageLoader'
import ProgramRewardsListCard from '@/components/ProgramRewards/program-rewards-list-card'
import useUtilityProgramRewards from '@/app/hooks/useUtilityProgramRewards'
import useMultiProgramFlowControl from '@/hooks/useMultiProgramFlowControl'
import {
  programsNewCollection,
  selectAvailableProgram,
  selectEnrolledProgram,
} from '@/reducers/programsNew'
import { useHasAtLeastOneDeviceEnrollment } from '@/app/hooks/useHasAtLeastOneDeviceEnrollment'

const listContent = (sponsorName: string, emailAddress: string | undefined) => [
  {
    icon: 'Share2',
    title: `Why is Optiwatt working with ${sponsorName}?`,
    description: `Optiwatt manages the technical connection to your vehicle on behalf of ${sponsorName}, adhering to the highest security standards.`,
  },
  {
    icon: 'Optiwatt',
    title: `What is Optiwatt?`,
    description: `Optiwatt connects your EV to demand response programs that help you save on charging, earn rewards, and support a cleaner grid. Join 20,000+ EVs that are enrolled across programs and counting!`,
  },
  {
    icon: 'Zap',
    title: "We'll set up your Optiwatt account",
    descriptionHtml: `Click <b>Connect Vehicle & Apply</b> to create your Optiwatt account using <b>${
      emailAddress ?? 'your email'
    }</b> and start the application for this program.`,
  },
]

function ProgramLogoComp(props: {
  programLogoSrc: string
  programName: string
}) {
  return (
    <div className="w-32 p-3">
      <img
        className="w-24"
        src={props.programLogoSrc}
        alt={props.programName}
      />
    </div>
  )
}

type AutogridConfirmationProps = {
  existingUser?: boolean
}

const connectVehiclePath = '/connect-vehicle?value_prop=false&pre_auth=true'

function getProgramFlowStartPath(utilityProgramId: number) {
  return `/multi-program-enrollment/${utilityProgramId}/device-selection`
}

function getConnectDevicePath(utilityProgramId: number) {
  return `/connect-device/vehicle?utilityProgramId=${utilityProgramId}`
}

function Actions({
  existingUser,
  handleProfileCreation,
  utilityProgramId,
  setFlowStartPoint,
}: {
  existingUser?: boolean
  handleProfileCreation: () => void
  utilityProgramId: number | undefined
  setFlowStartPoint: (pathname: string, programId: number) => void
}) {
  const navigation = useNavigation()

  const onContinue = () => {
    if (existingUser) {
      navigation.pushAddParam('show_login_modal', 'true')
      return
    }
    handleProfileCreation()
    if (utilityProgramId !== undefined) {
      setFlowStartPoint(
        getProgramFlowStartPath(utilityProgramId),
        utilityProgramId,
      )
      navigation.push(getConnectDevicePath(utilityProgramId))
    } else {
      navigation.push(connectVehiclePath)
    }
  }

  return (
    <div className="flex flex-col items-center w-full">
      <Button
        id="partner-confirmation-button"
        variant="primary"
        className="!mt-8"
        onClick={onContinue}
      >
        {existingUser ? 'Login to Continue' : 'Connect Vehicle & Apply'}
      </Button>
      {!existingUser && (
        <Text
          variant="link"
          className="flex items-center justify-center w-full mt-6"
          onClick={() => {
            navigation.pushAddParam('show_login_modal', 'true')
          }}
        >
          Have an existing Optiwatt account? Log in
        </Text>
      )}
    </div>
  )
}

export default function MultiProgramAutogridConfirmation({
  existingUser,
}: AutogridConfirmationProps) {
  const navigation = useNavigation()
  const dispatch = useAppDispatch()
  const user = useAppSelector((state) => state.user?.user ?? undefined)
  const existingUserPageOpen = useUrlSearchParam('show_login_modal') === 'true'
  const ExistingUserModalComponent = existingUser
    ? ExistingUserModal
    : ExistingUserModalSurvey
  const {
    config: programConfig,
    utilityProgramId,
    isLoading,
  } = useProgramSpecificConfigFromUrlParam()
  const { setFlowStartPoint } = useMultiProgramFlowControl()
  const { isLoading: isProgramsLoading } = programsNewCollection.useFetch()
  const availableProgram = useAppSelector(
    selectAvailableProgram(utilityProgramId ?? 0),
  )
  const viewConfig = availableProgram?.view_config_json
  const programName =
    viewConfig?.name ??
    availableProgram?.utility_program?.name ??
    programConfig.programName
  const sponsorName = viewConfig?.sponsorName ?? programName
  const programLogoSrc = viewConfig?.logoUrl ?? programConfig.programLogoSrc
  const { rewardsByCategory } = useUtilityProgramRewards(utilityProgramId ?? 0)
  const appDataDispatches = useInitialDataLoader(dispatch, false)
  const enrolledProgram = useAppSelector(
    selectEnrolledProgram(utilityProgramId ?? 0),
  )
  const hasAtLeastOneDeviceEnrollment =
    useHasAtLeastOneDeviceEnrollment(enrolledProgram)

  const handleProfileCreation = async () => {
    await fetchAuthenticated('autogrid/confirm_profile_creation', {
      method: 'POST',
    }).then((response) => {
      if (!response.ok) {
        Sentry.captureMessage('Failed to create profile for autogrid', {
          user,
          level: 'error',
        })
      }
    })
  }

  const handleAfterLogin = async (res: Response) => {
    const body = await res.json()
    await dispatch({
      type: auth.LOGIN_TOKEN_SUCCESS,
      payload: {
        access_token: body.access,
        refresh_token: body.refresh,
      },
    })
    await appDataDispatches()
    await handleProfileCreation()

    if (hasAtLeastOneDeviceEnrollment) {
      navigation.push('/devices')
    } else if (utilityProgramId !== undefined) {
      navigation.push(getProgramFlowStartPath(utilityProgramId))
    }
  }

  if (isLoading || isProgramsLoading) {
    return <PageLoader />
  }

  return (
    <NavigationPage
      id="autogrid-confirmation-nav-page"
      navigationOptions={['nothing']}
    >
      <NavigationPage.SingleCardContent>
        <div className="flex flex-col items-center w-full">
          <img src={Car} alt="Car" className="max-w-[min(24em,100%)]" />
          <div className="h-16 border-l-2 border-dashed border-themed-blue-500 -mb-7" />
          <LinkDiagram
            borderClassNames="border-themed-blue-500"
            logos={[
              OptiwattLogo,
              <ProgramLogoComp
                programLogoSrc={programLogoSrc}
                programName={programName}
              />,
            ]}
          />
        </div>
        <Text
          variant="h2"
          className={clsx({ '!mt-8': existingUser, '!mt-16': !existingUser })}
        >
          Connect Electric Vehicle
        </Text>
        <Text variant="body2" className="!mt-1">
          {sponsorName} is partnering with Optiwatt to connect to your vehicle.
        </Text>
        {rewardsByCategory && utilityProgramId !== undefined && (
          <div className="!mt-6 !mb-4">
            <ProgramRewardsListCard
              rewardsByCategory={rewardsByCategory}
              utilityProgramId={utilityProgramId}
            />
          </div>
        )}
        <List gap="sm" className="!mt-4">
          {listContent(sponsorName, user?.username)
            .slice(0, existingUser ? 2 : 3)
            .map((item, index) => (
              <Flex key={index} item>
                <IconDetailListItem
                  icon={() => (
                    <div className="flex items-center justify-center w-[32px] h-[32px] font-bold rounded-lg bg-themed-blue-100 text-[#1565C0]">
                      <Icon size={20} name={item.icon as IconNames} />
                    </div>
                  )}
                  title={item.title}
                  description={
                    'description' in item ? item.description : undefined
                  }
                  descriptionHtml={
                    'descriptionHtml' in item ? item.descriptionHtml : undefined
                  }
                />
              </Flex>
            ))}
        </List>
        {existingUser && (
          <AlertMessage className="mt-6" variant="info">
            We found an existing Optiwatt account linked to{' '}
            <b>{user?.username ?? 'your email'}</b>.
          </AlertMessage>
        )}
        <Actions
          existingUser={existingUser}
          handleProfileCreation={handleProfileCreation}
          utilityProgramId={utilityProgramId}
          setFlowStartPoint={setFlowStartPoint}
        />
      </NavigationPage.SingleCardContent>
      <ExistingUserModalComponent
        id="autogrid-existing-user-modal"
        open={existingUserPageOpen}
        afterLogin={existingUser ? handleAfterLogin : undefined}
        redirectUrl={`/devices`}
        onClose={() => {
          navigation.pushRemoveParam('show_login_modal')
        }}
      />
    </NavigationPage>
  )
}
